import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart3,
  Users,
  Settings,
  FileText,
  MessageSquare,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Calendar,
  Trash2,
  Plus,
  Send,
  Gift,
} from 'lucide-react';

const LAST_VISIT_KEY = 'inmonex_admin_last_visit';

const statusLabels = {
  trial: 'Пробный',
  active: 'Активный',
  expired: 'Истёк',
  new: 'Новый',
  in_progress: 'В работе',
  resolved: 'Решён',
  closed: 'Закрыт',
};

const formatUserOrgs = (user) => {
  const orgs = user.orgAccess || [];
  if (orgs.length === 0) return '\u2014';
  return orgs.map(oa => {
    const name = oa.org?.name || '?';
    const role = oa.role;
    const count = oa.org?._count?.users;
    return count != null ? `${name} (${role}, ${count} польз.)` : `${name} (${role})`;
  }).join(', ');
};

const isOwnerOfAny = (user) => (user.orgAccess || []).some(oa => oa.role === 'owner');

const AdminPanel = () => {
  // === API BASE URL ===
  const isProduction =
    window.location.protocol === 'https:' ||
    window.location.port === '' ||
    window.location.port === '80' ||
    window.location.port === '443';
  const BASE_URL = isProduction ? '/api' : 'http://' + window.location.hostname + ':5000/api';

  // === AUTHENTICATION STATE ===
  const [authStep, setAuthStep] = useState(1); // 1: login, 2: otp, 3: logged in
  const [loginForm, setLoginForm] = useState({ login: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [otpForm, setOtpForm] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');

  // === MAIN STATE ===
  const [activeTab, setActiveTab] = useState('dashboard');
  const [token, setToken] = useState(localStorage.getItem('inmonex_token') || '');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // === DASHBOARD STATE ===
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // === USERS STATE ===
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersStatus, setUsersStatus] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [editUserModal, setEditUserModal] = useState(null);
  const [editUserData, setEditUserData] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // === BONUS ADJUSTMENT MODAL ===
  const [adjustBonusModal, setAdjustBonusModal] = useState(null);
  const [adjustDelta, setAdjustDelta] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [adjustConfirmed, setAdjustConfirmed] = useState(false);
  const [adjustLoading, setAdjustLoading] = useState(false);

  // === BONUS STATS (sidebar section) ===
  const [bonusStats, setBonusStats] = useState(null);
  const [bonusStatsLoading, setBonusStatsLoading] = useState(false);

  // === SETTINGS STATE ===
  const [siteSettings, setSiteSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsForm, setSettingsForm] = useState(null);

  // === TICKETS STATE ===
  const [tickets, setTickets] = useState([]);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [ticketsTotalPages, setTicketsTotalPages] = useState(1);
  const [ticketsStatus, setTicketsStatus] = useState('');
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [ticketReplyText, setTicketReplyText] = useState('');
  const [ticketReplySending, setTicketReplySending] = useState(false);

  // === NEW TICKETS BADGE ===
  const [newTicketsCount, setNewTicketsCount] = useState(0);

  // === BACKUP STATE ===

  // === AUTH HANDLERS ===
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      const response = await fetch(BASE_URL + '/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...loginForm, login: loginForm.login.trim().toLowerCase() }),
      });

      const data = await response.json();

      if (!response.ok) {
        setAuthError(data.error || 'Ошибка входа');
        setAuthLoading(false);
        return;
      }

      if (!data.user?.isPlatformAdmin) {
        setAuthError('Доступ запрещён: вы не администратор платформы');
        setAuthLoading(false);
        return;
      }

      const jwt = data.accessToken || data.token;
      setToken(jwt);
      localStorage.setItem('inmonex_token', jwt);
      if (data.refreshToken) localStorage.setItem('inmonex_refresh_token', data.refreshToken);
      setAdminEmail(data.user.email);
      setAuthStep(2);
      setAuthLoading(false);

      // Auto-send OTP
      try {
        await fetch(BASE_URL + '/admin/send-otp', {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + jwt,
            'Content-Type': 'application/json',
          },
        });
      } catch (err) {
        console.error('Error sending OTP:', err);
      }
    } catch (error) {
      setAuthError('Ошибка сети: ' + error.message);
      setAuthLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      const response = await fetch(BASE_URL + '/admin/verify-otp', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: otpForm }),
      });

      const data = await response.json();

      if (!response.ok || !data.verified) {
        setAuthError('Неверный OTP-код');
        setAuthLoading(false);
        return;
      }

      setAuthStep(3);
      setAuthLoading(false);
    } catch (error) {
      setAuthError('Ошибка сети: ' + error.message);
      setAuthLoading(false);
    }
  };

  // ADMIN_OTP_REQUIRED (2026-06-13): OTP step-up живёт 30 мин и сбрасывается при
  // pm2 restart. Когда он истёк, защищённые admin-запросы возвращают 403 с этим кодом.
  // Раньше фронт это не ловил — данные молча не грузились. Теперь автоматически
  // возвращаем на OTP-форму и переотправляем код.
  const triggerOtpReauth = async () => {
    setOtpForm('');
    setAuthError('Сессия подтверждения истекла. Введите новый OTP-код.');
    setAuthStep(2);
    try {
      await fetch(BASE_URL + '/admin/send-otp', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
      });
    } catch (err) { console.error('Re-send OTP failed:', err); }
  };

  // Обёртка над fetch для защищённых admin-эндпоинтов: при 403 ADMIN_OTP_REQUIRED
  // запускает повторное OTP-подтверждение. Тело ответа не расходуется (clone).
  const adminFetch = async (url, opts) => {
    const response = await fetch(url, opts);
    if (response.status === 403) {
      const d = await response.clone().json().catch(() => ({}));
      if (d && d.code === 'ADMIN_OTP_REQUIRED') triggerOtpReauth();
    }
    return response;
  };

  const handleLogout = () => {
    setToken('');
    localStorage.removeItem('inmonex_token');
    setAuthStep(1);
    setLoginForm({ login: '', password: '' });
    setOtpForm('');
    setAuthError('');
  };

  // === FETCH DASHBOARD STATS ===
  useEffect(() => {
    if (authStep === 3 && activeTab === 'dashboard' && token) {
      fetchStats();
    }
  }, [authStep, activeTab, token]);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const lastVisit = localStorage.getItem(LAST_VISIT_KEY) || '';
      const params = lastVisit ? '?since=' + encodeURIComponent(lastVisit) : '';
      const response = await adminFetch(BASE_URL + '/admin/stats' + params, {
        headers: { 'Authorization': 'Bearer ' + token },
      });
      const data = await response.json();
      setStats(data);
      // Обновляем время последнего визита
      localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
    setStatsLoading(false);
  };

  // === FETCH USERS ===
  useEffect(() => {
    if (authStep === 3 && activeTab === 'users' && token) {
      fetchUsers();
    }
  }, [authStep, activeTab, token, usersPage, usersSearch, usersStatus, sortBy, sortOrder]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams({
        page: usersPage,
        limit: 20,
        search: usersSearch,
        status: usersStatus,
        sortBy: sortBy,
        order: sortOrder,
      });
      const response = await adminFetch(BASE_URL + '/admin/users?' + params, {
        headers: { 'Authorization': 'Bearer ' + token },
      });
      const data = await response.json();
      setUsers(data.users);
      setUsersTotal(data.total);
      setUsersTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
    setUsersLoading(false);
  };

  // === FETCH BONUS STATS ===
  useEffect(() => {
    if (authStep !== 3 || activeTab !== 'bonuses' || !token) return;
    setBonusStatsLoading(true);
    adminFetch(BASE_URL + '/admin/bonus-stats', {
      headers: { 'Authorization': 'Bearer ' + token },
    })
      .then((r) => r.json())
      .then((data) => {
        setBonusStats(data);
        setBonusStatsLoading(false);
      })
      .catch(() => {
        setBonusStatsLoading(false);
      });
  }, [authStep, activeTab, token]);

  const openEditUserModal = (user) => {
    setEditUserModal(user);
    setEditUserData({
      subscriptionStatus: user.subscriptionStatus,
      subscriptionExpiresAt: user.subscriptionExpiresAt?.substring(0, 10) || '',
      isPlatformAdmin: user.isPlatformAdmin,
    });
  };

  const saveUserChanges = async () => {
    if (!editUserModal || !editUserData) return;
    try {
      const response = await adminFetch(BASE_URL + '/admin/users/' + editUserModal.id, {
        method: 'PUT',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editUserData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.propagatedTo > 0) {
          alert('Подписка обновлена. Также обновлено у ' + result.propagatedTo + ' связанных пользователей');
        }
        setEditUserModal(null);
        fetchUsers();
      }
    } catch (error) {
      console.error('Error saving user:', error);
    }
  };

  // === FETCH SETTINGS ===
  useEffect(() => {
    if (authStep === 3 && activeTab === 'settings' && token && !siteSettings) {
      fetchSettings();
    }
  }, [authStep, activeTab, token, siteSettings]);

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await adminFetch(BASE_URL + '/admin/settings', {
        headers: { 'Authorization': 'Bearer ' + token },
      });
      const data = await response.json();
      setSiteSettings(data);
      setSettingsForm({
        priceFirstOrg: data.priceFirstOrg,
        priceExtraOrg: data.priceExtraOrg,
        trialDays: data.trialDays,
        discounts: data.discounts || {},
        landingTexts: data.landingTexts || {},
        companyInfo: data.companyInfo || '',
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        contactTelegram: data.contactTelegram,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
    setSettingsLoading(false);
  };

  const saveSettings = async () => {
    if (!settingsForm) return;
    setSettingsSaving(true);
    try {
      const response = await adminFetch(BASE_URL + '/admin/settings', {
        method: 'PUT',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settingsForm),
      });

      if (response.ok) {
        setSiteSettings(null);
        setSettingsForm(null);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    }
    setSettingsSaving(false);
  };

  // === FETCH TICKETS ===
  useEffect(() => {
    if (authStep === 3 && activeTab === 'tickets' && token) {
      fetchTickets();
    }
  }, [authStep, activeTab, token, ticketsPage, ticketsStatus]);

  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      const params = new URLSearchParams({
        page: ticketsPage,
        limit: 20,
        status: ticketsStatus,
      });
      const response = await adminFetch(BASE_URL + '/admin/tickets?' + params, {
        headers: { 'Authorization': 'Bearer ' + token },
      });
      const data = await response.json();
      setTickets(data.tickets);
      setTicketsTotal(data.total);
      setTicketsTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
    setTicketsLoading(false);
  };

  // === NEW TICKETS POLLING (every 30s) ===
  useEffect(() => {
    if (authStep !== 3 || !token) return;
    const fetchNewCount = async () => {
      try {
        const params = new URLSearchParams({ page: 1, limit: 1, status: 'new' });
        const response = await adminFetch(BASE_URL + '/admin/tickets?' + params, {
          headers: { 'Authorization': 'Bearer ' + token },
        });
        const data = await response.json();
        setNewTicketsCount(data.total || 0);
      } catch (e) { /* ignore */ }
    };
    fetchNewCount();
    const interval = setInterval(fetchNewCount, 30000);
    return () => clearInterval(interval);
  }, [authStep, token]);

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setTicketReplyText('');
    try {
      const response = await adminFetch(BASE_URL + '/admin/tickets/' + ticket.id, {
        headers: { 'Authorization': 'Bearer ' + token },
      });
      const data = await response.json();
      setTicketDetails(data);
    } catch (error) {
      console.error('Error fetching ticket:', error);
    }
  };

  const sendTicketReply = async () => {
    if (!ticketReplyText.trim() || !selectedTicket) return;
    setTicketReplySending(true);
    try {
      await adminFetch(BASE_URL + '/admin/tickets/' + selectedTicket.id + '/reply', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: ticketReplyText }),
      });
      setTicketReplyText('');
      await openTicket(selectedTicket);
    } catch (error) {
      console.error('Error sending reply:', error);
    }
    setTicketReplySending(false);
  };

  const updateTicketStatus = async (status) => {
    if (!selectedTicket) return;
    try {
      await adminFetch(BASE_URL + '/admin/tickets/' + selectedTicket.id, {
        method: 'PUT',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });
      setSelectedTicket({ ...selectedTicket, status });
      await openTicket({ ...selectedTicket, status });
      await fetchTickets();
    } catch (error) {
      console.error('Error updating ticket:', error);
    }
  };

  // C5 (2026-06-12): backup-через-API удалён — бэкапы только серверным cron (backup.sh).

  // === RENDER LOGIN FORM ===
  if (authStep === 1) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #334155, #1e293b)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '400px',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}
        >
          <h1 style={{ color: '#1e293b', fontSize: '28px', marginBottom: '30px', textAlign: 'center' }}>
            Админ-панель
          </h1>

          {authError && (
            <div
              style={{
                background: '#fef2f2',
                color: '#dc2626',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '20px',
                fontSize: '14px',
              }}
            >
              {authError}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                Логин
              </label>
              <input
                type="text"
                value={loginForm.login}
                onChange={(e) => setLoginForm({ ...loginForm, login: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#f9fafb',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#1e293b',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
                required
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                Пароль
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    paddingRight: '40px',
                    background: '#f9fafb',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#1e293b',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#6366f1',
                    cursor: 'pointer',
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={authLoading}
              style={{
                width: '100%',
                padding: '10px',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: authLoading ? 'not-allowed' : 'pointer',
                opacity: authLoading ? 0.6 : 1,
              }}
            >
              {authLoading ? 'Вход...' : 'Войти'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // === RENDER OTP FORM ===
  if (authStep === 2) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #334155, #1e293b)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '400px',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}
        >
          <h1 style={{ color: '#1e293b', fontSize: '28px', marginBottom: '10px', textAlign: 'center' }}>
            Подтверждение OTP
          </h1>
          <p style={{ color: '#6b7280', textAlign: 'center', marginBottom: '30px', fontSize: '14px' }}>
            Код отправлен на {adminEmail}
          </p>

          {authError && (
            <div
              style={{
                background: '#fef2f2',
                color: '#dc2626',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '20px',
                fontSize: '14px',
              }}
            >
              {authError}
            </div>
          )}

          <form onSubmit={handleVerifyOTP}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                OTP-код (6 цифр)
              </label>
              <input
                type="text"
                value={otpForm}
                onChange={(e) => setOtpForm(e.target.value.replace(/\D/g, '').substring(0, 6))}
                maxLength="6"
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#f9fafb',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#1e293b',
                  fontSize: '20px',
                  textAlign: 'center',
                  letterSpacing: '5px',
                  boxSizing: 'border-box',
                }}
                required
              />
            </div>

            <button
              type="submit"
              disabled={authLoading}
              style={{
                width: '100%',
                padding: '10px',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: authLoading ? 'not-allowed' : 'pointer',
                opacity: authLoading ? 0.6 : 1,
                marginBottom: '10px',
              }}
            >
              {authLoading ? 'Проверка...' : 'Подтвердить'}
            </button>

            <button
              type="button"
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '10px',
                background: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Назад к входу
            </button>
          </form>
        </div>
      </div>
    );
  }

  // === RENDER ADMIN PANEL ===
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#e2e8f0' }}>
      {/* SIDEBAR */}
      <div
        style={{
          width: sidebarOpen ? '250px' : '60px',
          background: '#1e293b',
          borderRight: '1px solid #334155',
          padding: '20px',
          transition: 'width 0.3s',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
          {sidebarOpen && <h2 style={{ color: '#a5b4fc', fontSize: '18px', margin: 0 }}>INMONEX</h2>}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              background: 'none',
              border: 'none',
              color: '#a5b4fc',
              cursor: 'pointer',
            }}
          >
            {sidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </button>
        </div>

        <nav style={{ flex: 1 }}>
          {[
            { id: 'dashboard', label: 'Панель управления', icon: BarChart3 },
            { id: 'users', label: 'Пользователи', icon: Users },
            { id: 'settings', label: 'Настройки', icon: Settings },
            { id: 'landing', label: 'Контент лендинга', icon: FileText },
            { id: 'tickets', label: 'Тикеты', icon: MessageSquare, badge: newTicketsCount },
            { id: 'bonuses', label: 'Бонусы', icon: Gift },
          ].map(({ id, label, icon: Icon, badge }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                width: '100%',
                padding: '12px',
                marginBottom: '8px',
                background: activeTab === id ? 'rgba(99,102,241,0.15)' : 'transparent',
                border: 'none',
                borderRadius: '6px',
                color: activeTab === id ? '#a5b4fc' : '#cbd5e1',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '14px',
                fontWeight: activeTab === id ? '500' : '400',
                transition: 'all 0.2s',
                position: 'relative',
              }}
            >
              <Icon size={20} />
              {sidebarOpen && label}
              {badge > 0 && (
                <span
                  style={{
                    position: sidebarOpen ? 'static' : 'absolute',
                    top: sidebarOpen ? undefined : '4px',
                    right: sidebarOpen ? undefined : '4px',
                    background: '#ef4444',
                    color: 'white',
                    borderRadius: '10px',
                    padding: '2px 7px',
                    fontSize: '11px',
                    fontWeight: '700',
                    marginLeft: sidebarOpen ? 'auto' : undefined,
                    minWidth: '18px',
                    textAlign: 'center',
                    lineHeight: '16px',
                  }}
                >
                  {badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            padding: '12px',
            background: 'transparent',
            border: '1px solid #334155',
            borderRadius: '6px',
            color: '#f87171',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '14px',
          }}
        >
          <LogOut size={20} />
          {sidebarOpen && 'Выйти'}
        </button>
      </div>

      {/* MAIN CONTENT */}
      <div style={{ flex: 1, padding: '30px', overflowY: 'auto' }}>
        {/* DASHBOARD TAB */}
        {activeTab === 'dashboard' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
              <h1 style={{ color: '#1e293b', fontSize: '32px', margin: 0 }}>
                Панель управления
              </h1>
              <a
                href="https://metrica.yandex.ru/dashboard?id=108524417"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '10px 20px',
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  textDecoration: 'none',
                }}
              >
                <BarChart3 size={16} />
                Яндекс Метрика
              </a>
            </div>

            {statsLoading ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>Загрузка...</div>
            ) : stats ? (
              <>
                {/* NEW SINCE LAST VISIT */}
                {(stats.newUsersSince > 0 || stats.newOrgsSince > 0 || stats.newRevenueSince > 0) && (
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #065f46, #064e3b)',
                      border: '1px solid #10b981',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '20px',
                      display: 'flex',
                      gap: '30px',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ color: '#a7f3d0', fontSize: '14px', fontWeight: '600' }}>
                      С последнего визита:
                    </div>
                    {stats.newUsersSince > 0 && (
                      <div style={{ color: 'white', fontSize: '14px' }}>
                        <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#34d399' }}>{stats.newUsersSince}</span>{' '}
                        новых пользователей
                      </div>
                    )}
                    {stats.newOrgsSince > 0 && (
                      <div style={{ color: 'white', fontSize: '14px' }}>
                        <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#34d399' }}>{stats.newOrgsSince}</span>{' '}
                        новых организаций
                      </div>
                    )}
                    {stats.newRevenueSince > 0 && (
                      <div style={{ color: 'white', fontSize: '14px' }}>
                        <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#34d399' }}>{stats.newRevenueSince.toFixed(2)}</span>{' '}
                        новый доход
                      </div>
                    )}
                  </div>
                )}

                {/* STAT CARDS */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                  {[
                    { label: 'Всего пользователей', value: stats.totalUsers },
                    { label: 'Активные подписки', value: stats.totalActiveSubscriptions },
                    { label: 'Пробный период', value: stats.totalTrialUsers },
                    { label: 'Истёкшие подписки', value: stats.totalExpiredUsers },
                    { label: 'Всего организаций', value: stats.totalOrgs },
                    { label: 'Новые тикеты', value: stats.newTicketsCount || 0 },
                    { label: 'Всего тикетов', value: stats.totalTickets || 0 },
                    { label: 'Среднее орг/пользователь', value: stats.avgOrgsPerUser || '0.0' },
                  ].map((stat, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '12px',
                        padding: '20px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                      }}
                    >
                      <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '10px' }}>{stat.label}</div>
                      <div style={{ color: '#1e293b', fontSize: '32px', fontWeight: 'bold' }}>{stat.value}</div>
                    </div>
                  ))}
                </div>

                {/* REGISTRATIONS CHART */}
                {stats.registrationsLast30Days && stats.registrationsLast30Days.length > 0 && (
                  <div
                    style={{
                      background: '#ffffff',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '30px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    }}
                  >
                    <h3 style={{ color: '#1e293b', fontSize: '18px', marginTop: 0, marginBottom: '20px' }}>
                      Регистрации за 30 дней
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '200px' }}>
                      {stats.registrationsLast30Days.map((item, idx) => {
                        const maxCount = Math.max(...stats.registrationsLast30Days.map((x) => x.count));
                        const height = (item.count / (maxCount || 1)) * 180;
                        return (
                          <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div
                              style={{
                                width: '100%',
                                height: height + 'px',
                                background: '#6366f1',
                                borderRadius: '4px 4px 0 0',
                              }}
                              title={item.date + ': ' + item.count}
                            />
                            <div style={{ color: '#6b7280', fontSize: '11px', marginTop: '8px' }}>
                              {item.date.substring(5)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* TOTAL REVENUE */}
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '30px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                >
                  <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '10px' }}>Общий доход</div>
                  <div style={{ color: '#22c55e', fontSize: '32px', fontWeight: 'bold' }}>
                    {stats.totalRevenue?.toFixed(2) || '0.00'}
                  </div>
                </div>

                {/* RECENT REGISTRATIONS */}
                {stats.recentRegistrations && stats.recentRegistrations.length > 0 && (
                  <div
                    style={{
                      background: '#ffffff',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    }}
                  >
                    <h3 style={{ color: '#1e293b', fontSize: '18px', marginTop: 0, marginBottom: '20px' }}>
                      Последние регистрации
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#f3f4f6' }}>
                            <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                              Логин
                            </th>
                            <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                              Имя
                            </th>
                            <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                              Статус
                            </th>
                            <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                              Орг.
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.recentRegistrations.map((user) => (
                            <tr key={user.id} style={{ borderBottom: '1px solid #e5e7eb', background: '#ffffff' }}>
                              <td style={{ color: '#374151', padding: '12px', fontSize: '14px' }}>{user.login}</td>
                              <td style={{ color: '#374151', padding: '12px', fontSize: '14px' }}>{user.name}</td>
                              <td style={{ padding: '12px' }}>
                                <span
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    background:
                                      user.subscriptionStatus === 'trial'
                                        ? '#3b82f6'
                                        : user.subscriptionStatus === 'active'
                                          ? '#22c55e'
                                          : '#ef4444',
                                    color: 'white',
                                  }}
                                >
                                  {statusLabels[user.subscriptionStatus] || user.subscriptionStatus}
                                </span>
                              </td>
                              <td style={{ color: '#6b7280', padding: '12px', fontSize: '14px' }}>
                                {user._count?.orgAccess || 0}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && (
          <div>
            <h1 style={{ color: '#1e293b', fontSize: '32px', margin: 0, marginBottom: '30px' }}>
              Пользователи
            </h1>

            {/* FILTERS */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Поиск по имени, логину, email или организации..."
                value={usersSearch}
                onChange={(e) => {
                  setUsersSearch(e.target.value);
                  setUsersPage(1);
                }}
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '10px',
                  background: '#f9fafb',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#1e293b',
                  fontSize: '14px',
                }}
              />
              <select
                value={usersStatus}
                onChange={(e) => {
                  setUsersStatus(e.target.value);
                  setUsersPage(1);
                }}
                style={{
                  padding: '10px',
                  background: '#f9fafb',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#6b7280',
                  fontSize: '14px',
                }}
              >
                <option value="">Все статусы</option>
                <option value="trial">Пробный</option>
                <option value="active">Активный</option>
                <option value="expired">Истёк</option>
              </select>
            </div>

            {/* USERS TABLE */}
            {usersLoading ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>Загрузка...</div>
            ) : users.length > 0 ? (
              <>
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    marginBottom: '20px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                >
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#f3f4f6' }}>
                          <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                            Логин
                          </th>
                          <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                            Имя
                          </th>
                          <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                            Email
                          </th>
                          <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                            Статус
                          </th>
                          <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                            Организации
                          </th>
                          <th
                            onClick={() => {
                              if (sortBy === 'createdAt') {
                                setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                              } else {
                                setSortBy('createdAt');
                                setSortOrder('desc');
                              }
                            }}
                            style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px', cursor: 'pointer', userSelect: 'none' }}
                          >
                            Зарегистрирован {sortBy === 'createdAt' && (sortOrder === 'desc' ? '▼' : '▲')}
                          </th>
                          <th
                            onClick={() => {
                              if (sortBy === 'bonusBalance') {
                                setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                              } else {
                                setSortBy('bonusBalance');
                                setSortOrder('desc');
                              }
                            }}
                            style={{ color: '#6b7280', textAlign: 'right', padding: '12px', fontWeight: '500', fontSize: '13px', cursor: 'pointer', userSelect: 'none' }}
                          >
                            Бонусы {sortBy === 'bonusBalance' && (sortOrder === 'desc' ? '▼' : '▲')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr
                            key={user.id}
                            onClick={() => openEditUserModal(user)}
                            style={{
                              borderBottom: '1px solid #e5e7eb',
                              background: '#ffffff',
                              cursor: 'pointer',
                              transition: 'background 0.2s',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
                          >
                            <td style={{ color: '#374151', padding: '12px', fontSize: '14px' }}>{user.login}</td>
                            <td style={{ color: '#374151', padding: '12px', fontSize: '14px' }}>{user.name}</td>
                            <td style={{ color: '#6b7280', padding: '12px', fontSize: '14px' }}>{user.email}</td>
                            <td style={{ padding: '12px' }}>
                              <span
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  background:
                                    user.subscriptionStatus === 'trial'
                                      ? '#3b82f6'
                                      : user.subscriptionStatus === 'active'
                                        ? '#22c55e'
                                        : '#ef4444',
                                  color: 'white',
                                }}
                              >
                                {statusLabels[user.subscriptionStatus] || user.subscriptionStatus}
                              </span>
                              {!isOwnerOfAny(user) && (
                                <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>(от владельца)</span>
                              )}
                            </td>
                            <td
                              style={{
                                color: '#6b7280',
                                padding: '12px',
                                fontSize: '14px',
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={formatUserOrgs(user)}
                            >
                              {formatUserOrgs(user)}
                            </td>
                            <td style={{ color: '#6b7280', padding: '12px', fontSize: '14px' }}>
                              {new Date(user.createdAt).toLocaleDateString()}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: Number(user.bonusBalance || 0) > 0 ? '#22c55e' : '#6b7280', fontSize: '13px' }}>
                              {Number(user.bonusBalance || 0).toLocaleString('ru-RU')} ₸
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* PAGINATION */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <button
                    onClick={() => setUsersPage(Math.max(1, usersPage - 1))}
                    disabled={usersPage === 1}
                    style={{
                      padding: '8px 12px',
                      background: usersPage === 1 ? '#d1d5db' : '#6366f1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: usersPage === 1 ? 'not-allowed' : 'pointer',
                      opacity: usersPage === 1 ? 0.5 : 1,
                    }}
                  >
                    Назад
                  </button>
                  <div style={{ color: '#6b7280', fontSize: '14px' }}>
                    Страница {usersPage} из {usersTotalPages}
                  </div>
                  <button
                    onClick={() => setUsersPage(Math.min(usersTotalPages, usersPage + 1))}
                    disabled={usersPage === usersTotalPages}
                    style={{
                      padding: '8px 12px',
                      background: usersPage === usersTotalPages ? '#d1d5db' : '#6366f1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: usersPage === usersTotalPages ? 'not-allowed' : 'pointer',
                      opacity: usersPage === usersTotalPages ? 0.5 : 1,
                    }}
                  >
                    Далее
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>Пользователи не найдены</div>
            )}

            {/* USER DETAIL MODAL */}
            {editUserModal && editUserData && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                }}
                onClick={() => setEditUserModal(null)}
              >
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    padding: '30px',
                    maxWidth: '560px',
                    width: '90%',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 style={{ color: '#1e293b', fontSize: '20px', marginTop: 0, marginBottom: '20px' }}>
                    Пользователь: {editUserModal.login}
                  </h2>

                  {/* INFO SECTION */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr',
                      gap: '8px 16px',
                      marginBottom: '20px',
                      padding: '16px',
                      background: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    <div style={{ color: '#6b7280', fontSize: '13px' }}>ID:</div>
                    <div style={{ color: '#1e293b', fontSize: '13px' }}>{editUserModal.id}</div>
                    <div style={{ color: '#6b7280', fontSize: '13px' }}>Логин:</div>
                    <div style={{ color: '#1e293b', fontSize: '13px' }}>{editUserModal.login}</div>
                    <div style={{ color: '#6b7280', fontSize: '13px' }}>Имя:</div>
                    <div style={{ color: '#1e293b', fontSize: '13px' }}>{editUserModal.name}</div>
                    <div style={{ color: '#6b7280', fontSize: '13px' }}>Email:</div>
                    <div style={{ color: '#1e293b', fontSize: '13px' }}>{editUserModal.email}</div>
                    <div style={{ color: '#6b7280', fontSize: '13px' }}>Зарегистрирован:</div>
                    <div style={{ color: '#1e293b', fontSize: '13px' }}>{new Date(editUserModal.createdAt).toLocaleString()}</div>
                  </div>

                  {/* BONUS BALANCE */}
                  <div style={{ marginBottom: '16px', padding: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Бонусный баланс</div>
                        <div style={{ fontSize: '20px', fontWeight: 800, color: '#111827' }}>
                          {Number(editUserModal.bonusBalance || 0).toLocaleString('ru-RU')} ₸
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAdjustBonusModal({ userId: editUserModal.id, login: editUserModal.login, currentBalance: Number(editUserModal.bonusBalance || 0) })}
                        style={{
                          padding: '8px 14px', background: '#6366f1', color: '#fff',
                          border: 'none', borderRadius: '6px', cursor: 'pointer',
                          fontSize: '13px', fontWeight: 600,
                        }}
                      >
                        Корректировать
                      </button>
                    </div>
                  </div>

                  {/* ORG LIST */}
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: '#1e293b', fontSize: '16px', marginTop: 0, marginBottom: '10px' }}>
                      Организации ({(editUserModal.orgAccess || []).length})
                    </h3>
                    {(editUserModal.orgAccess || []).length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {(editUserModal.orgAccess || []).map((oa) => (
                          <div
                            key={oa.org?.id || Math.random()}
                            style={{
                              padding: '8px 12px',
                              background: '#f9fafb',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              fontSize: '13px',
                              color: '#374151',
                            }}
                          >
                            {oa.org?.name || '?'} — <strong>{oa.role}</strong>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: '#6b7280', fontSize: '13px' }}>Нет организаций</div>
                    )}
                  </div>

                  {/* EDITABLE FIELDS */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                      Статус подписки
                    </label>
                    <select
                      value={editUserData.subscriptionStatus}
                      onChange={(e) => setEditUserData({ ...editUserData, subscriptionStatus: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#f9fafb',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1e293b',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="trial">Пробный</option>
                      <option value="active">Активный</option>
                      <option value="expired">Истёк</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                      Дата окончания подписки
                    </label>
                    <input
                      type="date"
                      value={editUserData.subscriptionExpiresAt}
                      onChange={(e) => setEditUserData({ ...editUserData, subscriptionExpiresAt: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#f9fafb',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1e293b',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={editUserData.isPlatformAdmin}
                      onChange={(e) => setEditUserData({ ...editUserData, isPlatformAdmin: e.target.checked })}
                      style={{ cursor: 'pointer' }}
                    />
                    <label style={{ color: '#6b7280', fontSize: '14px', cursor: 'pointer' }}>
                      Администратор платформы
                    </label>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={saveUserChanges}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: '#6366f1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                      }}
                    >
                      Сохранить
                    </button>
                    <button
                      onClick={() => setEditUserModal(null)}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: 'transparent',
                        color: '#6b7280',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer',
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      onClick={() => {
                        const d = new Date();
                        d.setDate(d.getDate() + 30);
                        setEditUserData({ ...editUserData, subscriptionExpiresAt: d.toISOString().substring(0, 10) });
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: 'transparent',
                        color: '#6366f1',
                        border: '1px solid #6366f1',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        marginTop: '4px',
                      }}
                    >
                      Продлить на 30 дней
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ADJUST BONUS MODAL */}
            {adjustBonusModal && (
              <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
              }}>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                  <h3 style={{ margin: 0, marginBottom: '6px', fontSize: '18px', color: '#111827' }}>Корректировка бонусов</h3>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                    Пользователь: <b>{adjustBonusModal.login}</b> · Текущий баланс: <b>{adjustBonusModal.currentBalance.toLocaleString('ru-RU')} ₸</b>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                      Изменение (положительное — начислить, отрицательное — списать)
                    </label>
                    <input
                      type="number"
                      value={adjustDelta}
                      onChange={(e) => setAdjustDelta(e.target.value)}
                      placeholder="Например: 500 или -300"
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                    {adjustDelta && !isNaN(Number(adjustDelta)) && Number(adjustDelta) !== 0 && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        Новый баланс будет: <b style={{ color: (adjustBonusModal.currentBalance + Number(adjustDelta)) < 0 ? '#ef4444' : '#22c55e' }}>
                          {(adjustBonusModal.currentBalance + Number(adjustDelta)).toLocaleString('ru-RU')} ₸
                        </b>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                      Причина (обязательно, минимум 3 символа)
                    </label>
                    <textarea
                      value={adjustNote}
                      onChange={(e) => setAdjustNote(e.target.value)}
                      placeholder="Например: компенсация после возврата платежа"
                      rows={3}
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '16px', cursor: 'pointer', padding: '10px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                    <input
                      type="checkbox"
                      checked={adjustConfirmed}
                      onChange={(e) => setAdjustConfirmed(e.target.checked)}
                      style={{ marginTop: '2px' }}
                    />
                    <span style={{ fontSize: '12px', color: '#92400e', lineHeight: '1.4' }}>
                      ⚠️ Я понимаю, что эта операция изменит бонусный баланс пользователя и будет записана в историю транзакций как административная корректировка.
                    </span>
                  </label>

                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      disabled={adjustLoading}
                      onClick={() => {
                        setAdjustBonusModal(null);
                        setAdjustDelta('');
                        setAdjustNote('');
                        setAdjustConfirmed(false);
                      }}
                      style={{ padding: '10px 16px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      disabled={adjustLoading || !adjustConfirmed || !adjustDelta || Number(adjustDelta) === 0 || !adjustNote || adjustNote.trim().length < 3}
                      onClick={async () => {
                        const delta = Number(adjustDelta);
                        if (isNaN(delta) || delta === 0) { alert('Введите ненулевое число'); return; }
                        if (!adjustNote.trim() || adjustNote.trim().length < 3) { alert('Укажите причину (минимум 3 символа)'); return; }
                        if (!adjustConfirmed) { alert('Подтвердите действие'); return; }
                        setAdjustLoading(true);
                        try {
                          const response = await adminFetch(BASE_URL + '/admin/users/' + adjustBonusModal.userId + '/adjust-bonus', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
                            body: JSON.stringify({ delta, note: adjustNote.trim(), confirmed: true }),
                          });
                          const data = await response.json();
                          if (!response.ok) throw new Error(data.error || 'Ошибка корректировки');
                          alert('Баланс обновлён: ' + Number(data.newBalance).toLocaleString('ru-RU') + ' ₸');
                          const updatedUserId = adjustBonusModal.userId;
                          setAdjustBonusModal(null);
                          setAdjustDelta('');
                          setAdjustNote('');
                          setAdjustConfirmed(false);
                          if (editUserModal && editUserModal.id === updatedUserId) {
                            setEditUserModal({ ...editUserModal, bonusBalance: data.newBalance });
                          }
                          fetchUsers();
                        } catch (err) {
                          alert('Ошибка: ' + err.message);
                        } finally {
                          setAdjustLoading(false);
                        }
                      }}
                      style={{
                        padding: '10px 16px',
                        background: (adjustLoading || !adjustConfirmed || !adjustDelta || Number(adjustDelta) === 0 || !adjustNote || adjustNote.trim().length < 3) ? '#9ca3af' : '#6366f1',
                        color: '#fff', border: 'none', borderRadius: '6px',
                        cursor: (adjustLoading || !adjustConfirmed) ? 'not-allowed' : 'pointer',
                        fontSize: '13px', fontWeight: 600,
                      }}
                    >
                      {adjustLoading ? 'Применяется...' : 'Применить'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <div>
            <h1 style={{ color: '#1e293b', fontSize: '32px', margin: 0, marginBottom: '30px' }}>
              Настройки сайта
            </h1>

            {settingsLoading ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>Загрузка...</div>
            ) : settingsForm ? (
              <>
                {/* PRICING SECTION */}
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                >
                  <h3 style={{ color: '#1e293b', fontSize: '18px', marginTop: 0, marginBottom: '20px' }}>Тарифы</h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                        Цена первой организации
                      </label>
                      <input
                        type="number"
                        value={settingsForm.priceFirstOrg}
                        onChange={(e) => setSettingsForm({ ...settingsForm, priceFirstOrg: parseFloat(e.target.value) })}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: '#f9fafb',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          color: '#1e293b',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                        Цена доп. организации
                      </label>
                      <input
                        type="number"
                        value={settingsForm.priceExtraOrg}
                        onChange={(e) => setSettingsForm({ ...settingsForm, priceExtraOrg: parseFloat(e.target.value) })}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: '#f9fafb',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          color: '#1e293b',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                        Пробный период (дни)
                      </label>
                      <input
                        type="number"
                        value={settingsForm.trialDays}
                        onChange={(e) => setSettingsForm({ ...settingsForm, trialDays: parseInt(e.target.value) })}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: '#f9fafb',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          color: '#1e293b',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* DISCOUNTS SECTION */}
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                >
                  <h3 style={{ color: '#1e293b', fontSize: '18px', marginTop: 0, marginBottom: '20px' }}>Скидки</h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                    {[1, 6, 12, 24].map((months) => (
                      <div key={months}>
                        <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                          {months} мес. (%)
                        </label>
                        <input
                          type="number"
                          value={settingsForm.discounts[months] || 0}
                          onChange={(e) =>
                            setSettingsForm({
                              ...settingsForm,
                              discounts: { ...settingsForm.discounts, [months]: parseFloat(e.target.value) },
                            })
                          }
                          style={{
                            width: '100%',
                            padding: '10px',
                            background: '#f9fafb',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            color: '#1e293b',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* CONTACTS SECTION */}
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                >
                  <h3 style={{ color: '#1e293b', fontSize: '18px', marginTop: 0, marginBottom: '20px' }}>Контакты и реквизиты</h3>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                      Реквизиты компании (HTML, отображается в футере лэндинга)
                    </label>
                    <textarea
                      value={settingsForm.companyInfo || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, companyInfo: e.target.value })}
                      rows={3}
                      placeholder={'ИП «Елемесова Айдана Боранбайкызы» · БИН/ИИН: 920207451098<br>г. Астана, ул. Е-67, 6, НП 15 · support@inmonex.kz'}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#f9fafb',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1e293b',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        boxSizing: 'border-box',
                        resize: 'vertical',
                      }}
                    />
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                      Можно использовать HTML-теги: &lt;br&gt; для переноса строки, &middot; для разделителя
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={settingsForm.contactEmail}
                        onChange={(e) => setSettingsForm({ ...settingsForm, contactEmail: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: '#f9fafb',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          color: '#1e293b',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                        Телефон
                      </label>
                      <input
                        type="tel"
                        value={settingsForm.contactPhone}
                        onChange={(e) => setSettingsForm({ ...settingsForm, contactPhone: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: '#f9fafb',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          color: '#1e293b',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                        Telegram
                      </label>
                      <input
                        type="text"
                        value={settingsForm.contactTelegram}
                        onChange={(e) => setSettingsForm({ ...settingsForm, contactTelegram: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: '#f9fafb',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          color: '#1e293b',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* META SECTION */}
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                >
                  <h3 style={{ color: '#1e293b', fontSize: '18px', marginTop: 0, marginBottom: '20px' }}>SEO Мета</h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                        Мета-заголовок
                      </label>
                      <input
                        type="text"
                        value={settingsForm.metaTitle}
                        onChange={(e) => setSettingsForm({ ...settingsForm, metaTitle: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: '#f9fafb',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          color: '#1e293b',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
                        Мета-описание
                      </label>
                      <input
                        type="text"
                        value={settingsForm.metaDescription}
                        onChange={(e) => setSettingsForm({ ...settingsForm, metaDescription: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: '#f9fafb',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          color: '#1e293b',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* SAVE BUTTON */}
                <button
                  onClick={saveSettings}
                  disabled={settingsSaving}
                  style={{
                    padding: '12px 30px',
                    background: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: settingsSaving ? 'not-allowed' : 'pointer',
                    opacity: settingsSaving ? 0.6 : 1,
                  }}
                >
                  {settingsSaving ? 'Сохранение...' : 'Сохранить настройки'}
                </button>
              </>
            ) : null}
          </div>
        )}

        {/* LANDING CONTENT TAB */}
        {activeTab === 'landing' && (
          <div>
            <h1 style={{ color: '#1e293b', fontSize: '32px', margin: 0, marginBottom: '30px' }}>
              Контент лендинга
            </h1>

            <div
              style={{
                background: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              <p style={{ color: '#6b7280', fontSize: '14px' }}>
                Управление контентом лендинга настроено, но требует дополнительной формы для редактирования. Этот раздел поддерживает редактирование мультиязычного контента (RU/KZ/EN) для заголовков, подзаголовков, CTA, функций и FAQ с полными CRUD-операциями.
              </p>
              <p style={{ color: '#6b7280', fontSize: '14px' }}>
                API-эндпоинты готовы обрабатывать POST/PUT/DELETE операции для элементов лендинга.
              </p>
            </div>
          </div>
        )}

        {/* TICKETS TAB */}
        {activeTab === 'tickets' && (
          <div>
            <h1 style={{ color: '#1e293b', fontSize: '32px', margin: 0, marginBottom: '30px' }}>
              Тикеты поддержки
            </h1>

            {selectedTicket && ticketDetails ? (
              // TICKET DETAIL VIEW
              <div>
                <button
                  onClick={() => {
                    setSelectedTicket(null);
                    setTicketDetails(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    background: 'transparent',
                    color: '#6366f1',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    marginBottom: '20px',
                  }}
                >
                  Назад к тикетам
                </button>

                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ color: '#1e293b', fontSize: '20px', margin: 0 }}>{ticketDetails.subject}</h2>
                    <span
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background:
                          selectedTicket.status === 'new'
                            ? '#6366f1'
                            : selectedTicket.status === 'in_progress'
                              ? '#f59e0b'
                              : selectedTicket.status === 'resolved'
                                ? '#22c55e'
                                : '#64748b',
                        color: 'white',
                      }}
                    >
                      {statusLabels[selectedTicket.status] || selectedTicket.status}
                    </span>
                  </div>

                  <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '20px' }}>
                    От: {ticketDetails.user?.name || 'Неизвестный'} ({ticketDetails.user?.email || 'unknown@example.com'})
                  </div>

                  {/* MESSAGES */}
                  <div
                    style={{
                      background: '#f3f4f6',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '20px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    {ticketDetails.messages?.map((msg, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          justifyContent: msg.isAdmin ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '70%',
                            padding: '12px',
                            borderRadius: '8px',
                            background: msg.isAdmin ? '#6366f1' : '#ffffff',
                            border: msg.isAdmin ? 'none' : '1px solid #e5e7eb',
                            color: msg.isAdmin ? 'white' : '#374151',
                            fontSize: '14px',
                          }}
                        >
                          {msg.text}
                          <div style={{ fontSize: '11px', color: msg.isAdmin ? 'rgba(255,255,255,0.7)' : '#9ca3af', marginTop: '6px' }}>
                            {new Date(msg.createdAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* REPLY INPUT */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                      <textarea
                        value={ticketReplyText}
                        onChange={(e) => setTicketReplyText(e.target.value)}
                        placeholder="Введите ответ..."
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: '#f9fafb',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          color: '#1e293b',
                          fontSize: '14px',
                          minHeight: '80px',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                        }}
                      />
                      <button
                        onClick={sendTicketReply}
                        disabled={!ticketReplyText.trim() || ticketReplySending}
                        style={{
                          padding: '10px 20px',
                          background: '#6366f1',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: ticketReplySending ? 'not-allowed' : 'pointer',
                          opacity: ticketReplySending ? 0.6 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <Send size={16} />
                        Отправить
                      </button>
                    </div>
                  </div>

                  {/* STATUS BUTTONS */}
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {['in_progress', 'resolved', 'closed'].map((status) => (
                      <button
                        key={status}
                        onClick={() => updateTicketStatus(status)}
                        style={{
                          padding: '8px 16px',
                          background: selectedTicket.status === status ? '#6366f1' : 'transparent',
                          color: selectedTicket.status === status ? 'white' : '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '13px',
                          cursor: 'pointer',
                        }}
                      >
                        {statusLabels[status] || status}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              // TICKETS LIST VIEW
              <>
                {/* STATUS FILTER */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  {['', 'new', 'in_progress', 'resolved', 'closed'].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setTicketsStatus(status);
                        setTicketsPage(1);
                      }}
                      style={{
                        padding: '8px 16px',
                        background: ticketsStatus === status ? '#6366f1' : 'transparent',
                        color: ticketsStatus === status ? 'white' : '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      {status === '' ? 'Все' : (statusLabels[status] || status)}
                    </button>
                  ))}
                </div>

                {/* TICKETS TABLE */}
                {ticketsLoading ? (
                  <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>Загрузка...</div>
                ) : tickets.length > 0 ? (
                  <>
                    <div
                      style={{
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        marginBottom: '20px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                      }}
                    >
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#f3f4f6' }}>
                              <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                                Тема
                              </th>
                              <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                                Пользователь
                              </th>
                              <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                                Статус
                              </th>
                              <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                                Дата
                              </th>
                              <th style={{ color: '#6b7280', textAlign: 'left', padding: '12px', fontWeight: '500', fontSize: '13px' }}>
                                Сообщения
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {tickets.map((ticket) => (
                              <tr
                                key={ticket.id}
                                onClick={() => openTicket(ticket)}
                                style={{
                                  borderBottom: '1px solid #e5e7eb',
                                  background: '#ffffff',
                                  cursor: 'pointer',
                                  transition: 'background 0.2s',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                                onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
                              >
                                <td style={{ color: '#374151', padding: '12px', fontSize: '14px' }}>{ticket.subject}</td>
                                <td style={{ color: '#6b7280', padding: '12px', fontSize: '14px' }}>{ticket.user?.name}</td>
                                <td style={{ padding: '12px' }}>
                                  <span
                                    style={{
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      background:
                                        ticket.status === 'new'
                                          ? '#6366f1'
                                          : ticket.status === 'in_progress'
                                            ? '#f59e0b'
                                            : ticket.status === 'resolved'
                                              ? '#22c55e'
                                              : '#64748b',
                                      color: 'white',
                                    }}
                                  >
                                    {statusLabels[ticket.status] || ticket.status}
                                  </span>
                                </td>
                                <td style={{ color: '#6b7280', padding: '12px', fontSize: '14px' }}>
                                  {new Date(ticket.createdAt).toLocaleDateString()}
                                </td>
                                <td style={{ color: '#6b7280', padding: '12px', fontSize: '14px' }}>
                                  {ticket._count?.messages || 0}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* PAGINATION */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                      <button
                        onClick={() => setTicketsPage(Math.max(1, ticketsPage - 1))}
                        disabled={ticketsPage === 1}
                        style={{
                          padding: '8px 12px',
                          background: ticketsPage === 1 ? '#d1d5db' : '#6366f1',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: ticketsPage === 1 ? 'not-allowed' : 'pointer',
                          opacity: ticketsPage === 1 ? 0.5 : 1,
                        }}
                      >
                        Назад
                      </button>
                      <div style={{ color: '#6b7280', fontSize: '14px' }}>
                        Страница {ticketsPage} из {ticketsTotalPages}
                      </div>
                      <button
                        onClick={() => setTicketsPage(Math.min(ticketsTotalPages, ticketsPage + 1))}
                        disabled={ticketsPage === ticketsTotalPages}
                        style={{
                          padding: '8px 12px',
                          background: ticketsPage === ticketsTotalPages ? '#d1d5db' : '#6366f1',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: ticketsPage === ticketsTotalPages ? 'not-allowed' : 'pointer',
                          opacity: ticketsPage === ticketsTotalPages ? 0.5 : 1,
                        }}
                      >
                        Далее
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>Тикеты не найдены</div>
                )}
              </>
            )}
          </div>
        )}

        {/* BONUSES TAB */}
        {activeTab === 'bonuses' && (
          <div style={{ padding: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '20px', color: '#111827' }}>Реферальная программа — статистика</h2>

            {bonusStatsLoading && <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Загрузка...</div>}

            {bonusStats && !bonusStatsLoading && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>Всего бонусов в системе</div>
                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#22c55e' }}>
                      {Number(bonusStats.totalBalanceInSystem || 0).toLocaleString('ru-RU')} ₸
                    </div>
                  </div>
                  <div style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>Всего транзакций</div>
                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#6366f1' }}>
                      {bonusStats.totalTransactions || 0}
                    </div>
                  </div>
                </div>

                <div style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, marginBottom: '12px', fontSize: '16px', color: '#111827' }}>Топ-10 по балансу бонусов</h3>
                  {(!bonusStats.topByBalance || bonusStats.topByBalance.length === 0) && (
                    <div style={{ color: '#9ca3af', fontSize: '13px' }}>Пока нет пользователей с бонусами</div>
                  )}
                  {bonusStats.topByBalance && bonusStats.topByBalance.map((u, idx) => (
                    <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: idx < bonusStats.topByBalance.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{u.login}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{u.name || '—'} · {u.email || '—'}</div>
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#22c55e' }}>
                        {Number(u.bonusBalance || 0).toLocaleString('ru-RU')} ₸
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                  <h3 style={{ margin: 0, marginBottom: '12px', fontSize: '16px', color: '#111827' }}>Топ-10 по количеству приглашённых</h3>
                  {(!bonusStats.topByReferrals || bonusStats.topByReferrals.length === 0) && (
                    <div style={{ color: '#9ca3af', fontSize: '13px' }}>Пока никто никого не пригласил</div>
                  )}
                  {bonusStats.topByReferrals && bonusStats.topByReferrals.map((u, idx) => (
                    <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: idx < bonusStats.topByReferrals.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{u.login}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{u.name || '—'} · {u.email || '—'}</div>
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#6366f1' }}>
                        {u._count?.referrals || 0} чел.
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
